import { get } from 'lodash';

import { Table, TBody, TElement, THead, TRow } from '@adeccoux/tag-ds';
import { useT } from '@features/oneOnboarding/hooks/useT';

import { MasterDataType } from '../../masterDataHelpers';
import { SelectedRowType } from '../../MasterDataSettings';
type Props<T> = {
  activeTab: MasterDataType;
  dataSource: T[];
  selectedRow?: T | null;
  handleSelectRow?: (selectedItem: T) => void;
};
const columns: {
  [key in MasterDataType]: {
    field: string;
    headerName: string;
    sorting: boolean;
    alternativeField?: string;
  }[];
} = {
  branch: [
    { field: 'name', headerName: 'Branch Name', sorting: true },
    { field: 'sid', headerName: 'SID', sorting: true },
    { field: 'nl', headerName: 'Native Label', sorting: false },
    { field: 'isActive', headerName: 'Is Active', sorting: true },
    { field: 'isValid', headerName: 'Is Valid', sorting: true },
    { field: 'phone', headerName: 'Phone', sorting: false },
    { field: 'fax', headerName: 'Fax', sorting: false },
    { field: 'email', headerName: 'Email', sorting: false },
    { field: 'web', headerName: 'Website', sorting: false },
    { field: 'regionId', headerName: 'Region ID', sorting: false },
    { field: 'roleCode', headerName: 'Role Code', sorting: false },
    { field: 'siteId', headerName: 'Site ID', sorting: false },
    { field: 'externalBranchId', headerName: 'External BranchId', sorting: false },
    { field: 'id', headerName: 'Id', sorting: false },
  ],
  site: [
    { field: 'name', headerName: 'Name', sorting: true },
    { field: 'branchName', headerName: 'Branch Name', sorting: true },
    { field: 'company', headerName: 'Company', sorting: true },
    { field: 'code', headerName: 'Code', sorting: true },
    { field: 'externalLink', headerName: 'External Link', sorting: true },
    { field: 'isValid', headerName: 'Is Valid', sorting: true },
    { field: 'id', headerName: 'Id', sorting: false },
  ],
  requisition: [
    { field: 'requisitionId', headerName: 'Requisition ID', sorting: true },
    { field: 'jobType.name', headerName: 'Job Type', sorting: false, alternativeField: 'jobTypeId' },
    { field: 'site.name', headerName: 'Site Name', sorting: true },
    { field: 'siteId', headerName: 'Site ID', sorting: false },
    { field: 'title', headerName: 'Title', sorting: false },
    { field: 'isValid', headerName: 'Valid', sorting: false },
    { field: 'link', headerName: 'Link', sorting: false },
    { field: 'createdAt', headerName: 'Created At', sorting: false },
    { field: 'updatedAt', headerName: 'Updated At', sorting: false },
  ],
  purchaseOrder: [
    { field: 'poNumber', headerName: 'Purcahse Order Number', sorting: true },
    { field: 'clientId', headerName: 'Client Id', sorting: false },
    { field: 'isValid', headerName: 'Valid', sorting: false },
    { field: 'createdAt', headerName: 'Created At', sorting: false },
    { field: 'updatedAt', headerName: 'Updated At', sorting: false },
  ],
  client: [
    { field: 'customerId', headerName: 'Customer Id', sorting: false },
    { field: 'site.name', alternativeField: 'siteId', headerName: 'Site Id', sorting: false },
    { field: 'isValid', headerName: 'Valid', sorting: false },
    { field: 'name', headerName: 'name', sorting: false },
    { field: 'id', headerName: 'id', sorting: false },
    { field: 'createdAt', headerName: 'Created At', sorting: false },
    { field: 'updatedAt', headerName: 'Updated At', sorting: false },
  ],
  jobType: [
    { field: 'name', headerName: 'Site Id', sorting: false },
    { field: 'isValid', headerName: 'Valid', sorting: false },
    { field: 'id', headerName: 'id', sorting: false },
    { field: 'createdAt', headerName: 'Created At', sorting: false },
    { field: 'updatedAt', headerName: 'Updated At', sorting: false },
  ],
};

function MasterDataTable<T extends SelectedRowType>({ activeTab, dataSource, selectedRow, handleSelectRow }: Props<T>) {
  const { t, i18n } = useT('candidate_recruiter', 'entry');
  const translate = (name: string, label: string) => {
    const key = `recruiter:GENERAL.CONFIGURATION.${name}`;
    return i18n.exists(key) ? t(key) : label;
  };
  const getRowId = (row: SelectedRowType) => {
    if (!row) return;
    if ('id' in row) return row.id;
    if ('requisitionId' in row) return row.requisitionId;
  };

  return (
    <Table>
      <THead>
        <TRow>
          {handleSelectRow && <TElement />}
          {columns[activeTab].map((key) => (
            <TElement key={key.field} style={{ postion: 'relative' }}>
              {translate(key.field, key.headerName)}
            </TElement>
          ))}
        </TRow>
      </THead>
      <TBody>
        {dataSource.map((item: T, index: number) => (
          <TRow key={getRowId(item)}>
            {handleSelectRow && (
              <TElement key={getRowId(item)}>
                <div className="checkbox-container">
                  <input
                    aria-label="meaningful label for accessibility purposes"
                    onChange={(e) => {
                      handleSelectRow(item);
                    }}
                    type="checkbox"
                    checked={getRowId(selectedRow!) === getRowId(item)}
                  />
                </div>
              </TElement>
            )}
            {columns[activeTab].map((key, idx) => {
              if (get(item, key.field) === true) {
                return (
                  <TElement key={key.field}>
                    <span className="material-icons right">check</span>
                  </TElement>
                );
              } else if (get(item, key.field) === false) {
                return (
                  <TElement key={key.field}>
                    <span className="material-icons right">close</span>
                  </TElement>
                );
              }
              return (
                <TElement key={key.field}>
                  {get(item, key.field) || (key.alternativeField && get(item, key.alternativeField, 'N/A'))}
                </TElement>
              );
            })}
          </TRow>
        ))}
      </TBody>
    </Table>
  );
}

export default MasterDataTable;
